import { postInfluencerOAuthConnect } from '../../redux/buzzbridge/actions';
import React, { useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Switch, TextField, Typography } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { SocialLogin } from '../SocialLogin/SocialLogin';
import { FacebookUpgradeConnect } from '../SocialLogin/FacebookUpgradeConnect';
import { resolveInstagramConnectProvider } from '../../utils/instagramOAuthProvider';
import { useSelector } from 'react-redux';
import { buzzBridgeConfigSelector } from '../../selectors/selectors';

import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { InstagramGlyph, TiktokGlyph, YoutubeGlyph } from '../BuzzBridge/SocialPlatformSvgs';
import {
    IG_AUTO_GOLD,
    IG_AUTO_MINT,
    IG_AUTO_MUTED,
    IG_AUTO_WHITE,
    igAutoGlassCardSx,
    igAutoTextFieldSx,
} from './instagramAutomationTheme';
import type { SocialPlatform } from '../../types/instagramAutomation.types';
import type { TiktokPostFields, YoutubePostFields } from './platformPostFields';
import { PlatformComposeFields } from './PlatformComposeFields';
import { PlatformConnectionSettingsModal } from './PlatformConnectionSettingsModal';
import { selectAutomationStatus } from '../../redux/socialPosts/selector';

const PLATFORM_LABELS: Record<SocialPlatform, string> = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
};

function PlatformIcon({ platform }: { platform: SocialPlatform }) {
    const sx = { width: 22, height: 22 };
    if (platform === 'instagram') return <InstagramGlyph sx={sx} color={IG_AUTO_WHITE} />;
    if (platform === 'tiktok') return <TiktokGlyph sx={sx} color={IG_AUTO_WHITE} />;
    return <YoutubeGlyph sx={sx} color={IG_AUTO_WHITE} />;
}

export interface PostAutomationPlatformCardProps {
    platform: SocialPlatform;
    enabled: boolean;
    onEnabledChange: (v: boolean) => void;
    connected: boolean;
    facebookUpgradeRequired?: boolean;
    username?: string;
    onRefreshConnections: () => void;
    publishStatus?: 'idle' | 'working' | 'done' | 'failed';
    publishError?: string;
    /** Instagram DM automation — only for instagram */
    showAutomation?: boolean;
    automationEnabled?: boolean;
    onAutomationEnabledChange?: (v: boolean) => void;
    automationReady?: boolean;
    automationActive?: boolean;
    onConnectAutomation?: () => void;
    connectingAutomation?: boolean;
    triggerWord?: string;
    onTriggerWordChange?: (v: string) => void;
    dmMessage?: string;
    onDmMessageChange?: (v: string) => void;
    publicReply?: string;
    onPublicReplyChange?: (v: string) => void;
    stats?: { comments_handled: number; last_handled_at: string | null } | null;
    sharedCaption?: string;
    youtube?: YoutubePostFields;
    onYoutubeChange?: (patch: Partial<YoutubePostFields>) => void;
    tiktok?: TiktokPostFields;
    onTiktokChange?: (patch: Partial<TiktokPostFields>) => void;
    tiktokMediaPreviewUrl?: string;
    tiktokMediaDurationSec?: number;
    tiktokCreatorNickname?: string;
    /** Override default influencer OAuth (e.g. brand-auth/exchange). */
    onOAuthCode?: (code: string, provider: string) => Promise<void>;
    /** When set (brand flows), OAuth popup + upgrade use this auth base URL. */
    authBaseUrl?: string;
}

export function PostAutomationPlatformCard({
    platform,
    enabled,
    onEnabledChange,
    connected,
    facebookUpgradeRequired = false,
    username,
    onRefreshConnections,
    publishStatus = 'idle',
    publishError,
    showAutomation,
    automationEnabled,
    onAutomationEnabledChange,
    automationReady,
    automationActive,
    onConnectAutomation,
    connectingAutomation,
    triggerWord,
    onTriggerWordChange,
    dmMessage,
    onDmMessageChange,
    publicReply,
    onPublicReplyChange,
    stats,
    sharedCaption = '',
    youtube,
    onYoutubeChange,
    tiktok,
    onTiktokChange,
    tiktokMediaPreviewUrl,
    tiktokMediaDurationSec,
    tiktokCreatorNickname,
    onOAuthCode,
    authBaseUrl,
}: PostAutomationPlatformCardProps) {
    const dispatchPromise = useDispatchPromise();
    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    const automationStatus = useSelector(selectAutomationStatus);
    const igConnectProvider = platform === 'instagram' ? resolveInstagramConnectProvider(buzzBridgeConfig) : platform;
    const [settingsOpen, setSettingsOpen] = useState(false);

    const exchangeOAuth = (code: string, provider: string) => {
        if (onOAuthCode) return onOAuthCode(code, provider);
        return dispatchPromise(postInfluencerOAuthConnect({ code, provider })).then(() => onRefreshConnections());
    };

    const isBrandFlow = Boolean(authBaseUrl || onOAuthCode);
    const hasInfluencerProfile = Boolean(automationStatus?.has_influencer_profile);

    const statusLabel = (() => {
        if (publishStatus === 'done') return 'Posted';
        if (publishStatus === 'working') return 'Publishing…';
        if (publishStatus === 'failed') return 'Failed';
        if (automationActive) return 'Live sync';
        if (connected) return username ? `@${username}` : 'Connected';
        return 'Not connected';
    })();

    const statusColor =
        publishStatus === 'done' || automationActive
            ? IG_AUTO_MINT
            : publishStatus === 'failed'
            ? '#ff7351'
            : connected
            ? IG_AUTO_GOLD
            : IG_AUTO_MUTED;

    return (
        <Box
            sx={{
                ...igAutoGlassCardSx,
                mb: 2,
                opacity: enabled ? 1 : 0.65,
                ...(automationActive && { boxShadow: '0 0 15px rgba(255, 201, 101, 0.2)' }),
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: '#201f1f',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <PlatformIcon platform={platform} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: IG_AUTO_WHITE }}>
                            {PLATFORM_LABELS[platform]}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 10,
                                color: statusColor,
                                textTransform: 'uppercase',
                                letterSpacing: '0.12em',
                                fontWeight: 700,
                            }}>
                            {statusLabel}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {connected && (
                        <IconButton
                            size="small"
                            aria-label={`${PLATFORM_LABELS[platform]} settings`}
                            onClick={() => setSettingsOpen(true)}
                            sx={{ color: IG_AUTO_MUTED }}>
                            <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    )}
                    <Switch
                        checked={enabled}
                        onChange={e => onEnabledChange(e.target.checked)}
                        disabled={!connected}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: IG_AUTO_GOLD },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#feb700' },
                        }}
                    />
                </Box>
            </Box>

            {connected && platform === 'instagram' && facebookUpgradeRequired && (
                <Box sx={{ mb: 1.5 }}>
                    <FacebookUpgradeConnect
                        compact
                        authBaseUrl={authBaseUrl}
                        onCodeReceived={code => {
                            exchangeOAuth(code, 'meta')
                                .then(() => onRefreshConnections())
                                .catch((err: any) => {
                                    showToastMessage(
                                        err?.data?.message || err?.message || 'Could not connect Facebook.',
                                        'error',
                                    );
                                });
                        }}
                        onAuthComplete={() => onRefreshConnections()}
                    />
                </Box>
            )}
            {!connected && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <SocialLogin
                        provider={igConnectProvider}
                        authBaseUrl={authBaseUrl}
                        onCodeReceived={code => {
                            exchangeOAuth(code, igConnectProvider)
                                .then(() => {
                                    if (onOAuthCode) onRefreshConnections();
                                })
                                .catch((err: any) => {
                                    const msg =
                                        err?.data?.message ||
                                        err?.response?.data?.message ||
                                        err?.message ||
                                        'Could not connect this account. Try again.';
                                    showToastMessage(msg, 'error');
                                });
                        }}
                        onAuthComplete={() => onRefreshConnections()}
                        customButton={
                            <Button
                                size="small"
                                sx={{
                                    background: `linear-gradient(90deg, ${IG_AUTO_GOLD}, #feb700)`,
                                    color: '#392700',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    borderRadius: 999,
                                    px: 3,
                                    py: 1,
                                    textTransform: 'none',
                                }}>
                                Connect {PLATFORM_LABELS[platform]}
                            </Button>
                        }
                    />
                </Box>
            )}

            {publishError && (
                <Typography sx={{ fontSize: 12, color: '#ff7351', mb: 1, lineHeight: 1.4 }}>{publishError}</Typography>
            )}

            {connected && enabled && (
                <PlatformComposeFields
                    platform={platform}
                    sharedCaption={sharedCaption}
                    youtube={youtube}
                    onYoutubeChange={onYoutubeChange}
                    tiktok={tiktok}
                    onTiktokChange={onTiktokChange}
                    tiktokActive={platform === 'tiktok' && connected && enabled}
                    tiktokMediaPreviewUrl={platform === 'tiktok' ? tiktokMediaPreviewUrl : undefined}
                    tiktokMediaDurationSec={platform === 'tiktok' ? tiktokMediaDurationSec : undefined}
                    tiktokCreatorNickname={platform === 'tiktok' ? tiktokCreatorNickname : undefined}
                />
            )}

            {showAutomation && connected && enabled && (
                <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', pt: 2, mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography sx={{ fontSize: 13, color: IG_AUTO_WHITE, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            Comment-to-DM automation
                        </Typography>
                        <Switch
                            checked={Boolean(automationEnabled)}
                            onChange={e => onAutomationEnabledChange?.(e.target.checked)}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: IG_AUTO_GOLD },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#feb700' },
                            }}
                        />
                    </Box>

                    {automationEnabled && !automationReady && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: 12, color: IG_AUTO_MUTED, mb: 1, lineHeight: 1.4 }}>
                                Extra Instagram permissions are required for automatic DMs and comment replies.
                            </Typography>
                            <Button
                                disabled={connectingAutomation}
                                onClick={onConnectAutomation}
                                size="small"
                                sx={{
                                    color: IG_AUTO_GOLD,
                                    border: '1px solid rgba(255,201,101,0.35)',
                                    borderRadius: 999,
                                    textTransform: 'none',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                }}>
                                {connectingAutomation ? (
                                    <CircularProgress size={16} sx={{ color: IG_AUTO_GOLD }} />
                                ) : (
                                    'Enable comment automation'
                                )}
                            </Button>
                        </Box>
                    )}

                    {automationEnabled && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <TextField
                                label="Trigger comment (exact match)"
                                size="small"
                                value={triggerWord ?? ''}
                                onChange={e => onTriggerWordChange?.(e.target.value)}
                                placeholder="LINK or LINK,SEND"
                                fullWidth
                                sx={igAutoTextFieldSx}
                                helperText="Exact match after trim — comma-separated for multiple."
                                FormHelperTextProps={{ sx: { color: IG_AUTO_MUTED, fontSize: 11 } }}
                            />
                            <TextField
                                label="DM message"
                                size="small"
                                multiline
                                minRows={2}
                                value={dmMessage ?? ''}
                                onChange={e => onDmMessageChange?.(e.target.value)}
                                fullWidth
                                sx={igAutoTextFieldSx}
                            />
                            <TextField
                                label="Public reply (optional)"
                                size="small"
                                value={publicReply ?? ''}
                                onChange={e => onPublicReplyChange?.(e.target.value)}
                                fullWidth
                                sx={igAutoTextFieldSx}
                            />
                            {stats && (
                                <Typography sx={{ fontSize: 12, color: IG_AUTO_MUTED }}>
                                    {stats.comments_handled} comments handled
                                    {stats.last_handled_at
                                        ? ` · last ${new Date(stats.last_handled_at).toLocaleString()}`
                                        : ''}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            )}
            {connected && (
                <PlatformConnectionSettingsModal
                    open={settingsOpen}
                    onClose={() => setSettingsOpen(false)}
                    platform={platform}
                    username={username}
                    status={platform === 'instagram' ? automationStatus : null}
                    hasInfluencerProfile={hasInfluencerProfile}
                    onOAuthCode={onOAuthCode}
                    authBaseUrl={authBaseUrl}
                    showCreatorProfileOption={!isBrandFlow}
                    onUpdated={() => onRefreshConnections()}
                />
            )}
        </Box>
    );
}
